import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from "@rails/request.js"

import CustomLoadingOverlay from "../src/javascripts/nowsignage/tables-loading-overlay";
import CustomNoRowsOverlay from "../src/javascripts/nowsignage/tables-no-rows-overlay";
import CustomComparatorFunctions from "../src/javascripts/nowsignage/tables-custom-comparator-functions";
import CustomFormatterFunctions from "../src/javascripts/nowsignage/tables-custom-formatter-functions";
import CustomFilterValueGetterFunctions from "../src/javascripts/nowsignage/tables-custom-filter-value-getter-functions";

import { AllCommunityModule, ModuleRegistry, createGrid, themeQuartz } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';

import DOMPurify from 'dompurify';

LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-090595}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{NowSignage}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{NowSignage}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{NowSignage}_need_to_be_licensed___{NowSignage}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{26_June_2026}____[v3]_[0102]_MTc4MjQyODQwMDAwMA==3b13b2ef80749631c8486a224fefaacd");

ModuleRegistry.registerModules([AllEnterpriseModule, AllCommunityModule]);

export default class extends Controller {
  static targets = ['table', 'actionButton', 'selectionWrapper', 'actionsWrapper', 'selectAllButton', 'deselectAllButton', 'selectionInfo', 'selectionCount', 'tableToggle', 'hideIcon', 'showIcon', 'settingsModal', 'columnVisibilityForm'];
  static values = {
    modelName: { type: String, default: null },
    datasourceUrl: { type: String, default: null },
    columns: { type: Array, default: null },
    pagination: { type: Boolean, default: true },
    rowData: { type: Array, default: null },
    domLayout: { type: String, default: 'autoHeight' },
    autoSizeStrategy: { type: String, default: null },
    pagination: { type: Boolean, default: true },
    paginationPageSize: { type: Number, default: 25 },
    paginationPageSizeSelector: { type: Array, default: [25, 50, 100] },
    paginationAutoPageSize: { type: Boolean, default: false },
    rowHeight: { type: Number, default: null},
    rowDrag: { type: Boolean, default: false },
    rowSelection: { type: Boolean, default: true },
    filterMapping: { type: Array },
    multifilterFiltersMapping: { type: Object, default: null },
    clientSideSort: { type: Boolean, default: false },
    noRowsMessage: { type: String, default: "No result to show" },
    createButtonPath: String,
    noRowsCreateButtonText: { type: String, default: "Create New" },
    createButtonPathIsModal: { type: Boolean, default: true },
    blockSize: { type: Number, default: 25 },
    folderId: { type: Number, default: null },
    loopId: { type: String, default: null },
    adBooker: { type: Boolean, default: false },
    initialFilterModel: { type: Object, default: null }
  }

  async connect() {
    this.isTableInitialized = false;
    this.totalRowCount = 0;
    this.localeText = await this.getLocaleText();
    this.columnState = null;
    this.sortChangedTimeout = null;
    this.minRowHeight = 36;
    this.currentRowHeight = this.minRowHeight;
    this.autosizeOrderTimeout = null;
    this.sortActive = false;
    this.filterActive = false;
    this.dragHoveredRowNode = null;

    // Ensure theme mode is set on initial load
    this.initializeTheme();
    
    //Initialize the grid
    if((this.hasDatasourceUrlValue && this.datasourceUrlValue) || (this.hasRowDataValue && this.rowDataValue)) {
      this.initializeGrid();
    }
  }

  disconnect() {
    if (this.grid) {
      this.grid.destroy();
    }

    // Clean up debounce timeouts on disconnect
    if (this.sortChangedTimeout) {
      clearTimeout(this.sortChangedTimeout);
    }
    if (this.autosizeOrderTimeout) {
      clearTimeout(this.autosizeOrderTimeout);
    }
  }

  initializeGrid() {
    const htmlCellRenderer = (params) => {
      const outerDiv = document.createElement('div');
      outerDiv.classList.add('flex', 'items-center', 'h-full', 'min-w-0');
      const innerDiv = document.createElement('div');
      innerDiv.classList.add('overflow-hidden', 'text-ellipsis', 'whitespace-nowrap', 'leading-normal');
      innerDiv.innerHTML = DOMPurify.sanitize(params.value ?? '');
      outerDiv.appendChild(innerDiv);
      return outerDiv;
    };

    const htmlCellRendererBadge = (params) => {
      const outerDiv = document.createElement('div');
      outerDiv.classList.add('flex', 'items-center', 'h-full', 'min-w-0');
      const innerDiv = document.createElement('div');
      innerDiv.classList.add('flex', 'items-start','text-ellipsis', 'whitespace-nowrap', 'leading-normal');
      innerDiv.innerHTML = DOMPurify.sanitize(params.value ?? '');
      outerDiv.appendChild(innerDiv);
      return outerDiv;
    };

    const htmlCellRendererTooltip = (params) => {
      const div = document.createElement('div');
      div.classList.add('flex', 'items-center', 'h-full');
      div.setAttribute('data-controller', 'tooltip-loader');
      div.setAttribute('data-tooltip-loader-tooltip-placement-value', 'auto left');
      div.innerHTML = DOMPurify.sanitize(params.value ?? '');
      return div;
    };

    const htmlCellRendererFullCellWidth = (params) => {
      const div = document.createElement('div');
      div.classList.add('flex', 'items-center', 'h-full', 'w-full');
      div.innerHTML = DOMPurify.sanitize(params.value ?? '');
      return div;
    };

    const htmlCellRendererImage = (params) => {
      const div = document.createElement('div');
      div.classList.add('flex', 'items-center', 'justify-center', 'h-full', 'w-full');
      div.innerHTML = DOMPurify.sanitize(params.value ?? '');
      return div;
    }

    const valueMapper = (mappingValues) => {
      return (params) => {
        return mappingValues[params.value] || params.value;
      };
    };

    let columnDefs = [...this.columnsValue];

    if(this.hasFilterMappingValue){
      this.filterMappingValue.forEach(mapping => {
        if(mapping.field && mapping.values) {
          const columnIndex = columnDefs.findIndex(col => col.field === mapping.field);
          if(columnDefs[columnIndex]) {
            const column = columnDefs[columnIndex];

            if (column.filter === 'agMultiColumnFilter' && column.filterParams && column.filterParams.filters) {
              // For multi-filters, find which child filter matches the mapping
              column.filterParams.filters.forEach((childFilter, index) => {
                const childFieldName = this.getChildFilterFieldName(mapping.field, index) || mapping.field;
                
                // Apply valueFormatter only if this child filter matches the mapping
                if (childFieldName === mapping.field) {
                  childFilter.filterParams = {
                    ...childFilter.filterParams,
                    valueFormatter: valueMapper(mapping.values)
                  };
                }
              });
            } else {
              column.filterParams = {
                ...column.filterParams,
                valueFormatter: valueMapper(mapping.values)
              };
            }
          }
        }
      });
    }

    // Resolve strings coming in the column definition to actual comparator or formatter functions
    // AG Grid expects suppressHeaderKeyboardEvent to be a callback, not a boolean
    columnDefs.forEach(column => {
      if (column.comparator && typeof column.comparator === 'string' && CustomComparatorFunctions[column.comparator]) {
        column.comparator = CustomComparatorFunctions[column.comparator];
      }
      
      if (column.valueFormatter && typeof column.valueFormatter === 'string' && CustomFormatterFunctions[column.valueFormatter]) {
        column.valueFormatter = CustomFormatterFunctions[column.valueFormatter];
      }

      if (column.suppressHeaderKeyboardEvent === true) {
        column.suppressHeaderKeyboardEvent = () => true;
      }

      if (column.filterParams && column.filterParams.filters) {
        column.filterParams.filters.forEach(filter => {
          if (filter.filterValueGetter && typeof filter.filterValueGetter === 'string' && CustomFilterValueGetterFunctions[filter.filterValueGetter]) {
            filter.filterValueGetter = CustomFilterValueGetterFunctions[filter.filterValueGetter];
          }
        });
      }
    });

    const myTheme = themeQuartz
      .withParams({
        accentColor: "#03A5D2",
        fontFamily: "inherit",
        fontSize: 12,
        headerFontSize: 12,
        headerFontWeight: 700,
        headerVerticalPaddingScale: 0.75,
        rowVerticalPaddingScale: 0.9,
        spacing: 8,
        cellVerticalPaddingScale: 0.8,
        menuFontSize: 12,
        headerColumnBorderHeight: '60%',
      })
      .withParams({
        backgroundColor: "#FFFFFF",
        foregroundColor: "#1F2937",
        borderColor: "#EEEEEE",
        headerBackgroundColor: "#EEEEEE",
        menuBackgroundColor: "#FFFFFF",
        menuBorderColor: "#EEEEEE",
        selectedRowBackgroundColor: "#FAFAFA",
        headerColumnBorder: { color: '#d1d5db' },
        browserColorScheme: "light",
      }, 'light')
      .withParams({
        backgroundColor: "#1c1c21",
        foregroundColor: "#e5e5e5",
        borderColor: "#2a2a31",
        headerBackgroundColor: "#2a2a31",
        menuBackgroundColor: "#2a2a31",
        menuBorderColor: "#1c1c21",
        selectedRowBackgroundColor: "#374151",
        headerColumnBorder: { color: '#4B5563' },
        browserColorScheme: "dark",
      }, 'dark');
    
    // Set default grid options
    const gridOptions = {
      theme: myTheme,
      domLayout: this.domLayoutValue,
      columnDefs: columnDefs,
      localeText: this.localeText,
      serverSideEnableClientSideSort: this.clientSideSortValue,
      rowDragEntireRow: false, // Only allow dragging from the drag handle to prevent blocking horizontal scroll on mobile
      suppressContextMenu: true,
      suppressDragLeaveHidesColumns: true,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      pagination: this.paginationValue,
      rowBuffer: 0,
      rowClassRules: {
        'expired-row': (params) => {
          return params.data?.expired === true;
        },
        processing_by_bulk_action: (params) => params.data?.processing_by_bulk_action === true
      },
      getRowId: (params) => String(params.data.id),

      components: {
        htmlCellRenderer: htmlCellRenderer,
        htmlCellRendererBadge: htmlCellRendererBadge,
        htmlCellRendererTooltip: htmlCellRendererTooltip,
        htmlCellRendererFullCellWidth: htmlCellRendererFullCellWidth,
        htmlCellRendererImage: htmlCellRendererImage,
        customNoRowsOverlay: CustomNoRowsOverlay,
        customLoadingOverlay: CustomLoadingOverlay,
      },
      
      loadingOverlayComponent: 'customLoadingOverlay',
      loadingOverlayComponentParams: { loadingMessage: "Loading data..." },
      
      noRowsOverlayComponent: 'customNoRowsOverlay',
      noRowsOverlayComponentParams: {
        noRowsMessage: this.noRowsMessageValue,
        createButtonPath: this.createButtonPathValue,
        createButtonText: this.noRowsCreateButtonTextValue,
        createButtonPathIsModal: this.createButtonPathIsModalValue
      },
    };

    // Set server side or client side row model 
    if(this.hasDatasourceUrlValue && this.datasourceUrlValue) {
      gridOptions.rowModelType = 'serverSide';
      gridOptions.serverSideDatasource = this.createDatasource();
      gridOptions.cacheBlockSize = this.blockSizeValue;
      gridOptions.serverSideEnableClientSideSort = this.clientSideSortValue;
    }
    else {
      gridOptions.rowData = this.rowDataValue;
    }

    if(this.hasAutoSizeStrategyValue && this.autoSizeStrategyValue !== null) {
      gridOptions.autoSizeStrategy = {
        type: this.autoSizeStrategyValue,
      };
    }

    // Set up onGridReady callback
    gridOptions.onGridReady = (params) => {
      // Apply initial filter model if provided
      if(this.hasInitialFilterModelValue && this.initialFilterModelValue !== null) {
        // Example: {created_at: { dateFrom: "2025-04-01 00:00:00", dateTo: "2025-11-27 23:59:59", filterType: "date", type: "inRange" }}
        params.api.setFilterModel(this.initialFilterModelValue);
      }
      
      // Handle row height for normal layout
      if(this.domLayoutValue === 'normal' && this.paginationValue === false) {
        this.minRowHeight = params.api.getSizesForCurrentTheme().rowHeight;
        this.currentRowHeight = this.minRowHeight;
      }
    };

    if(this.domLayoutValue === 'normal' && this.paginationValue === false) {
      gridOptions.onFirstDataRendered = this.updateRowHeight.bind(this);
      gridOptions.onGridSizeChanged = this.updateRowHeight.bind(this);
      gridOptions.getRowHeight = (params) => {
        return this.currentRowHeight;
      };
    }

    // Set event listeners
    gridOptions.onFilterChanged = this.onFilterChanged.bind(this);
    gridOptions.onSortChanged = this.onSortChanged.bind(this);
    gridOptions.onPaginationChanged = this.onPaginationChanged.bind(this);
    gridOptions.onColumnMoved = (event) => {
      if (event.finished) {
        this.updateColumnState();
      }
    };
    gridOptions.onColumnResized = (event) => {
      if (event.finished && event.source === 'uiColumnResized') {
        this.updateColumnState();
      }
    };
    gridOptions.onColumnVisible = () => {};
    gridOptions.onColumnPinned = () => {};

    if(this.paginationValue) {
      gridOptions.paginationPageSize = this.paginationPageSizeValue;
      gridOptions.paginationPageSizeSelector = this.paginationPageSizeSelectorValue;
      gridOptions.paginationAutoPageSize = this.paginationAutoPageSizeValue;
    }
    
    // Set selection options
    if(this.rowSelectionValue) {
      gridOptions.selectionColumnDef = {
        sortable: true,
        resizable: false,
        width: 48,
        suppressHeaderMenuButton: false,
        headerTooltip: "Checkboxes indicate selection",
        lockPosition: "left",
        pinned: "left"
      }
      gridOptions.rowSelection = { 
        mode: "multiRow",
        isRowSelectable: (rowNode) => {
          // Check if the row data has a selectable field, default to true if not present
          return rowNode.data?.selectable !== false;
        }
      }
      gridOptions.onSelectionChanged = this.updateSelectedIds.bind(this);
      gridOptions.onRowSelected = this.updateSelectedIds.bind(this);
      gridOptions.rowDragMultiRow = true;
    }

    // Set row height
    if(this.hasRowHeightValue) {
      gridOptions.rowHeight = this.rowHeightValue;
    }

    if(this.rowDragValue) { // Handle row dragging events and functionality
      gridOptions.onRowDragEnter = (event) => {
        // console.log('Row drag enter:', event);
      };
      gridOptions.onRowDragMove = (event) => {
        if (event.overNode) {
          if (this.dragHoveredRowNode && this.dragHoveredRowNode !== event.overNode) {
            this.removeRowDragHighlight(this.dragHoveredRowNode);
          }
          
          this.addRowDragHighlight(event.overNode);
          this.dragHoveredRowNode = event.overNode;
        }
      };
      gridOptions.onRowDragLeave = (event) => {
        if (this.dragHoveredRowNode) {
          this.removeRowDragHighlight(this.dragHoveredRowNode);
          this.dragHoveredRowNode = null;
        }
      };
      gridOptions.onRowDragEnd = (event) => {
        if (this.dragHoveredRowNode) {
          this.removeRowDragHighlight(this.dragHoveredRowNode);
          this.dragHoveredRowNode = null;
        }
        this.handleRowDragEnd(event);
      };
    }

    this.grid = createGrid(this.tableTarget, gridOptions);
    this.isTableInitialized = true;
  }

  async exportToExcel() {
    if (!this.grid) {
      console.error('Grid not initialized');
      return;
    }

    const stripHtmlTags = (value) => {
      if (value === null || value === undefined) {
        return '';
      }
      
      if (typeof value !== 'string') {
        return value;
      }
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = value;
      return tempDiv.textContent || tempDiv.innerText || '';
    };

    await this.exportAllDataAsExcel(stripHtmlTags);
  }

  async exportAllDataAsExcel(stripHtmlTags) {
    let tempGrid = null;
    let tempContainer = null;
    
    try {
      this.grid.setGridOption('loading', true);

      // Fetch all data
      const allRows = await this.fetchAllData();

      // Get column definitions from the main grid
      const columnDefs = this.grid.getGridOption('columnDefs');

      // Create a temporary hidden container for export grid
      tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.width = '1px';
      tempContainer.style.height = '1px';
      tempContainer.style.overflow = 'hidden';
      document.body.appendChild(tempContainer);

      // Create temporary grid with client-side row model for export
      tempGrid = createGrid(tempContainer, {
        columnDefs: columnDefs,
        rowData: allRows,
        domLayout: 'normal',
        suppressColumnVirtualisation: true,
        suppressRowVirtualisation: true,
        headerHeight: 0,
        rowHeight: 0,
        suppressMovableColumns: true,
        suppressColumnMenu: true,
        suppressContextMenu: true,
        suppressHorizontalScroll: true,
        suppressVerticalScroll: true,
      });

      // Wait for grid to be ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Export
      tempGrid.exportDataAsExcel({
      exportAsExcelTable: true,
      excelTableConfig: {
        name: 'AG-GRID-TABLE',
        showFilterButton: 'match',
        showRowStripes: true,
        showColumnStripes: false,
        highlightFirstColumn: false,
        highlightLastColumn: false
      },
        processCellCallback: (params) => stripHtmlTags(params.value)
      });

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      if (tempGrid) tempGrid.destroy();
      if (tempContainer?.parentNode) tempContainer.parentNode.removeChild(tempContainer);
      this.grid.setGridOption('loading', false);
    }
  }

  async fetchAllData() {
    // If we have rowData (client-side), return it directly
    if (this.hasRowDataValue && this.rowDataValue) {
      return this.rowDataValue;
    }

    // Otherwise, fetch from server
    const filterModel = this.grid.getFilterModel();
    const columnState = this.grid.getColumnState();
    const sortedColumn = columnState.find(col => col.sort !== null && col.sort !== undefined);
    const parsedFilters = this.parseFilters(filterModel);

    const request = {
      startRow: 0,
      endRow: this.totalRowCount || 999999,
      order_by: sortedColumn ? sortedColumn.colId : null,
      direction: sortedColumn ? sortedColumn.sort : null,
      search: parsedFilters
    };

    // Add params from the URL to the search query
    const splitedUrl = this.datasourceUrlValue.split('?');
    const url = new URL(splitedUrl[0], window.location.origin);
    const currentParams = new URLSearchParams(splitedUrl[1]);

    currentParams.forEach((value, key) => {
      const match = key.match(/^search(?:\[(\w+)\])?(?:\[(\w+)\])?(?:\[\])?$/);
      
      if (match) {
        const subKey = match[1];
        const nestedKey = match[2];
        
        if (nestedKey) {
          // Handle two-level nested parameters like search[last_connected_at][from]
          if (!request.search[subKey]) {
            request.search[subKey] = {};
          }
          request.search[subKey][nestedKey] = value;
        } else if (key.endsWith('[]')) {
          // Handle array parameters like search[licence_type][]
          if (!request.search[subKey]) {
            request.search[subKey] = [];
          }
          request.search[subKey].push(value);
        } else if (subKey) {
          // Handle single-level parameters like search[query]
          request.search[subKey] = value;
        }
      } else {
        request[key] = value;
      }
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      throw new Error('Failed to fetch data for export');
    }

    const data = await response.json();
    return data.rows || [];
  }

  async exportToCsv() {
    if (!this.grid) {
      console.error('Grid not initialized');
      return;
    }

    const processCellForCsv = (value) => {
      if (value === null || value === undefined) {
        return '';
      }
      
      // Handle arrays - process each item and join only if multiple values
      if (Array.isArray(value)) {
        const processedItems = value
          .map(item => {
            if (item === null || item === undefined) return '';
            const itemText = typeof item === 'string' ? item : String(item);
            // Strip HTML and clean up
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = itemText;
            return (tempDiv.textContent || tempDiv.innerText || '').trim();
          })
          .filter(item => item !== '');
        
        // Only join with " / " if there are multiple items
        return processedItems.length > 1 ? processedItems.join(' / ') : (processedItems[0] || '');
      }
      
      // Convert to string if not already
      let text = typeof value === 'string' ? value : String(value);
      text = text.replace(/<br\s*\/?>/gi, '\n');
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      text = tempDiv.innerText || tempDiv.textContent || '';
      
      // Split by line breaks and filter out empty lines
      const lines = text
        .split(/\r\n|\n|\r/)
        .map(line => line.trim())
        .filter(line => line !== '');
      
      if (lines.length > 1) {
        const cleanedLines = lines.map(line => line.replace(/\s*\/\s*$/, ''));
        return cleanedLines.join(' / ');
      } else if (lines.length === 1) {
        return lines[0].replace(/\s+/g, ' ');
      }
      
      return '';
    };

    await this.exportAllDataAsCsv(processCellForCsv);
  }

  async exportAllDataAsCsv(stripHtmlTags) {
    let tempGrid = null;
    let tempContainer = null;
    
    try {
      this.grid.setGridOption('loading', true);

      // Fetch all data
      const allRows = await this.fetchAllData();

      // Get column definitions from the main grid
      const columnDefs = this.grid.getGridOption('columnDefs');

      // Create a temporary hidden container for export grid
      tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.width = '1px';
      tempContainer.style.height = '1px';
      tempContainer.style.overflow = 'hidden';
      document.body.appendChild(tempContainer);

      // Create temporary grid with client-side row model for export
      tempGrid = createGrid(tempContainer, {
        columnDefs: columnDefs,
        rowData: allRows,
        domLayout: 'normal',
        suppressColumnVirtualisation: true,
        suppressRowVirtualisation: true,
        headerHeight: 0,
        rowHeight: 0,
        suppressMovableColumns: true,
        suppressColumnMenu: true,
        suppressContextMenu: true,
        suppressHorizontalScroll: true,
        suppressVerticalScroll: true,
      });

      // Wait for grid to be ready
      await new Promise(resolve => setTimeout(resolve, 100));

      // Export
      tempGrid.exportDataAsCsv({
        processCellCallback: (params) => stripHtmlTags(params.value),
        suppressQuotes: false,
        columnSeparator: ','
      });

    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      if (tempGrid) tempGrid.destroy();
      if (tempContainer?.parentNode) tempContainer.parentNode.removeChild(tempContainer);
      this.grid.setGridOption('loading', false);
    }
  }

  createDatasource() {
    return {
      getRows: (params) => {
        const request = {
          startRow: params.request.startRow,
          endRow: params.request.endRow,
          order_by: params.request.sortModel.length > 0 ? params.request.sortModel[0].colId : null,
          direction: params.request.sortModel.length > 0 ? params.request.sortModel[0].sort : null,
          search: this.parseFilters(params.request.filterModel)
        };

        // Add params from the URL to the search query
        const splitedUrl = this.datasourceUrlValue.split('?')
        const url = new URL(splitedUrl[0], window.location.origin);
        const currentParams = new URLSearchParams(splitedUrl[1]);

        currentParams.forEach((value, key) => {
          const match = key.match(/^search(?:\[(\w+)\])?(?:\[(\w+)\])?(?:\[\])?$/);
          
          if (match) {
            const subKey = match[1];
            const nestedKey = match[2];

            if (nestedKey) {
              // Handle two-level nested parameters like search[last_connected_at][from]
              if (!request.search[subKey]) {
                request.search[subKey] = {};
              }
              request.search[subKey][nestedKey] = value;
            } else if (key.endsWith('[]')) {
              // Handle array parameters like search[licence_type][]
              if (!request.search[subKey]) {
                request.search[subKey] = [];
              }
              request.search[subKey].push(value);
            } else if (subKey) {
              // Handle single-level parameters like search[query]
              request.search[subKey] = value;
            }
          } else {
            request[key] = value;
          }
        });

        if (this.grid) {
          this.grid.setGridOption('loading', true);
        }
        
        fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify(request)
        })
        .then(response => response.json())
        .then(data => {                    
          this.totalRowCount = data.count || 0;
          
          if (!data.rows || data.rows.length === 0) {
            params.success({
              rowData: [],
              rowCount: 0
            });
            this.grid.setGridOption('loading', false);
            this.grid.showNoRowsOverlay();
          } else {
            params.success({
              rowData: data.rows,
              rowCount: data.count
            });
            this.grid.setGridOption('loading', false);
            // Autosize order column after new data loads (debounced)
            this.autosizeOrderColumn();
          }
        })
        .catch(error => {
          console.error('Server-Side Error:', error);
          // Hide loading overlay on error
          if (this.grid) {
            this.grid.setGridOption('loading', false);
          }
          params.fail();
        });
      }
    };
  }

  onFilterChanged() {
    if(this.rowSelectionValue) {
      this.deselectAllEntries();
    }
    this.filterActive = this.grid.isAnyFilterPresent();
    
    // Suppress row drag if either sort or filter is active
    const suppressRowDrag = this.sortActive || this.filterActive;
    this.grid.setGridOption('suppressRowDrag', suppressRowDrag);
  }

  onSortChanged() {
    // Clear any previous existing debounce timeout
    if (this.sortChangedTimeout) {
      clearTimeout(this.sortChangedTimeout);
    }
    
    // Set a new timeout to debounce rapid clicks
    this.sortChangedTimeout = setTimeout(() => {
      this.updateColumnState();
    }, 500);

    const colState = this.grid.getColumnState() || [];
    this.sortActive = colState.some((c) => c.sort);
    
    // Suppress row drag if either sort or filter is active
    const suppressRowDrag = this.sortActive || this.filterActive;
    this.grid.setGridOption('suppressRowDrag', suppressRowDrag);
  }

  onPaginationChanged(event) {
    if (event.newPageSize) {
      const newPageSize = event.api.paginationGetPageSize();
      // Only set cacheBlockSize for server-side row model
      if (this.hasDatasourceUrlValue && this.datasourceUrlValue) {
        this.grid.setGridOption('cacheBlockSize', newPageSize);
      }
    }
  }

  updateSelectedIds() {
    let selectedRows = [];
    let selectedCount = 0;

    const selectionState = this.grid.getServerSideSelectionState();
    
    //Get the selected rows from the table and send the event
    if(selectionState) {
      if(selectionState.selectAll) {
        if(selectionState.toggledNodes.length === 0) {
          selectedCount = this.totalRowCount;
          selectedRows = Array.from({ length: selectedCount }, (_, index) => ({ 
            id: index
          }));
        } 
        else {
          selectedCount = this.totalRowCount - (selectionState.toggledNodes ? selectionState.toggledNodes.length : 0);
          selectedRows = Array.from({ length: selectedCount }, (_, index) => ({ 
            id: index
          }));
        }
      }
      else {
        if (selectionState.toggledNodes) {
          selectedCount = selectionState.toggledNodes.length;
          selectedRows = selectionState.toggledNodes.map((nodeId, index) => ({
            id: nodeId
          }));
        }
      }
    }

    const event = new CustomEvent("rowsSelected", {
      detail: { selectedRows }
    });
    window.dispatchEvent(event);

    this.updateSelectionButtons(selectionState, selectedCount);
    this.updateActionButtons(selectionState, selectedCount);
  }

  async updateColumnState(reset = false) {
    const columnState = this.grid.getColumnState();
    let tableColumnState = null;

    if(reset) {
      tableColumnState = {
        tables: {
          [this.tableTarget.id]: []
        }
      }
    }
    else{
      if(columnState.length > 0 && columnState !== this.columnState) {
        const columns = [];
        columnState.forEach((column, index) => {
          if((column.colId !== 'actions' && column.colId !== 'ag-Grid-SelectionColumn') || column.pinned === null) {
            columns.push({
              field: column.colId,
              hide: column.hide || false,
              flex: column.flex || null,
              width: column.width || null,
              sort: column.sort || null,
              sortIndex: column.sortIndex || null,
            });
          }     
        });
        
        tableColumnState = {
          tables: {
            [this.tableTarget.id]: columns
          }
        }
      }
    }
        
    if(tableColumnState){
      const success = await this.updateCustomerUIPreferences(tableColumnState);
      if(reset && success) {
        window.location.reload();
      }
    }

    this.columnState = columnState;
  }

  async updateCustomerUIPreferences(tableColumnState) {
    const request = new FetchRequest("patch", "/customer", {
      body: {
        customer: {
          ui_preferences: tableColumnState
        }
      },
      responseKind: "json"
    });
    
    try {
      const response = await request.perform();
      if (!response.ok) {
        console.error('Failed to update customer setting');
        return false;
      }
      else {
        return true;
      }
    } catch (error) {
      console.error('Error updating customer setting:', error);
      return false;
    }
  }

  resetTableSettings() {
    this.updateColumnState(true);
  }

  selectAllEntries() {
    this.grid.setServerSideSelectionState({
      selectAll: true,
      toggledNodes: []
    });
    this.updateSelectedIds();
  }

  deselectAllEntries() {
    this.grid.setServerSideSelectionState({
      selectAll: false,
      toggledNodes: []
    });
    this.updateSelectedIds();
  }

  getTableRequestData() {
    const filterModel = this.grid.getFilterModel();
    const parsedFilters = this.parseFilters(filterModel);
    
    // Get any extra params 
    let extraParams = {};
    if (this.datasourceUrlValue) {
      try {
        const url = new URL(this.datasourceUrlValue, window.location.origin);
        const filterParamMap = {
          query: 'search[query]',
          filter_by: 'search[filter_by]',
          global_licence_type: 'search[global_licence_type][]',
          file_type: 'search[file_type]',
          orientation: 'search[orientation]',
          access_tag_list: 'search[access_tag_list][]'
        };

        Object.entries(filterParamMap).forEach(([key, param]) => {
          const isArray = param.endsWith('[]');
          const value = isArray ? url.searchParams.getAll(param) : url.searchParams.get(param);

          if (value && (isArray ? value.length > 0 : true)) {
            extraParams[key] = value;
          }
        });
      } catch (error) {
        console.warn('Failed to parse datasource URL:', error);
      }
    }
    else {
      extraParams = {
        search: { ...parsedFilters }
      }
    }
    
    return {
      search: { ...parsedFilters, ...extraParams }
    };
  }

  updateActionButtons(selectionState, selectedCount) {
    const baseParams = new URLSearchParams();

    const reset = selectedCount === 0;
    
    if(!reset) {
      //Get the current request of the table to add the filters to the action buttons
      const tableRequestData = this.getTableRequestData();
      if (tableRequestData) {
        Object.entries(tableRequestData).forEach(([key, value]) => {
          if (this.isNotEmpty(value)) {
            if (Array.isArray(value)) {
              value.forEach(item => baseParams.append(`search[${key}]`, item));
            } else {
              if(typeof value === 'object') {
                Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                  if (Array.isArray(nestedValue)) {
                    nestedValue.forEach(item => baseParams.append(`search[${nestedKey}][]`, item));
                  } else {
                    if(typeof nestedValue === 'object') { // For filters with multiple attributes (like last_connected_at with type, from, to)
                      Object.entries(nestedValue).forEach(([deepNestedKey, deepNestedValue]) => {
                        baseParams.append(`search[${nestedKey}][${deepNestedKey}]`, deepNestedValue);
                      });
                    } else {
                      baseParams.append(`search[${nestedKey}]`, nestedValue);
                    }
                  }
                });
              } else {
                baseParams.append(`search[${key}]`, value);
              }
            }
          }
        });
      }
      
      if (selectionState) {
        selectionState.selectAll ? baseParams.set('select_all', true) : baseParams.set('select_all', false);
        if(selectionState.toggledNodes.length > 0) {
          selectionState.selectAll ? baseParams.set('exclude_ids', selectionState.toggledNodes.join(',')) : baseParams.set('item_ids', selectionState.toggledNodes.join(','))
        }
      }

      if(this.hasModelNameValue) {
        baseParams.set('type', this.modelNameValue);
      }
      if(this.hasAdBookerValue && this.adBookerValue != false) {
        baseParams.set('ad_booker', this.adBookerValue);
      }
      if(this.hasFolderIdValue && this.folderIdValue != null) {
        baseParams.set('folder_id', this.folderIdValue);
      }
      if(this.hasLoopIdValue && this.loopIdValue != null) {
        baseParams.set('loop_id', this.loopIdValue);
      }
    }

    if(this.hasActionButtonTarget) {
      for(const button of this.actionButtonTargets) {
        if(button.href) {
          const cleanHref = button.href.split('?')[0];
          button.href = `${cleanHref}?${baseParams.toString()}`;
        }
      }
    }

    if(selectedCount > 0 && this.hasActionButtonTarget){
      this.actionButtonTargets.forEach(link => {
        link.classList.remove('pointer-events-none', 'opacity-50', 'cursor-not-allowed');
      });
    }else{
      this.actionButtonTargets.forEach(link => {
        link.classList.add('pointer-events-none', 'opacity-50', 'cursor-not-allowed');
      });
    }
  }

  updateSelectionButtons(selectionState, selectedCount) {
    if(this.hasSelectionCountTarget) {
      this.selectionCountTarget.textContent = selectedCount;
    }

    if (selectionState.selectAll) {
      // All rows selected - show deselect button, hide select button
      if (this.hasSelectAllButtonTarget && this.hasDeselectAllButtonTarget) {
        this.selectAllButtonTarget.classList.add('hidden');
        this.deselectAllButtonTarget.classList.remove('hidden');
      }
    } else {
      // Not all rows selected - show select button, hide deselect button
      if (this.hasSelectAllButtonTarget && this.hasDeselectAllButtonTarget) {
        this.selectAllButtonTarget.classList.remove('hidden');
        this.deselectAllButtonTarget.classList.add('hidden');
      }
    }

    // Partial selection
    if(selectionState.toggledNodes && selectionState.toggledNodes.length > 0) {
      if (this.hasSelectAllButtonTarget && this.hasDeselectAllButtonTarget) {
        this.selectAllButtonTarget.classList.remove('hidden');
        this.deselectAllButtonTarget.classList.remove('hidden');
      }
    }
  }

  onFirstDataRendered(params) {
    this.updateRowHeight(params);
  }

  onGridSizeChanged(params) {
    this.updateRowHeight(params);
  }

  updateRowHeight = (params) => {
    // resetRowHeights is only available for client-side row model
    if (this.hasDatasourceUrlValue) {
      return;
    }

    // Get the height of the grid body - this excludes the height of the headers
    const bodyViewport = this.tableTarget.querySelector(".ag-body-viewport");
    if (!bodyViewport) {
      return;
    }
    const gridHeight = bodyViewport.clientHeight;

    const renderedRowCount = params.api.getDisplayedRowCount();
  
    // if the rendered rows * min height is greater than available height, just just set the height
    // to the min and let the scrollbar do its thing
    if (renderedRowCount * this.minRowHeight >= gridHeight) {
      if (this.currentRowHeight !== this.minRowHeight) {
        this.currentRowHeight = this.minRowHeight;
        params.api.resetRowHeights();
      }
    } else {
      // set the height of the row to the grid height / number of rows available
      this.currentRowHeight = Math.floor(gridHeight / renderedRowCount);
      params.api.resetRowHeights();
    }
  };

  toggleTable(event) {
    const wrapper = this.tableTarget.closest('.grid');
  
    if(wrapper){
      if(wrapper.classList.contains('grid-template-rows-[1fr]')) {
        wrapper.classList.remove('grid-template-rows-[1fr]');
        wrapper.classList.add('grid-template-rows-[0fr]');
        if(this.hasHideIconTarget && this.hasShowIconTarget){
          this.hideIconTarget.classList.add('hidden');
          this.showIconTarget.classList.remove('hidden', 'pointer-events-none');
        }
        if(this.hasSettingsModalTarget){
          this.settingsModalTarget.classList.add('opacity-0', 'pointer-events-none');
        }
        if(this.hasSelectionWrapperTarget){
          this.selectionWrapperTarget.classList.add('opacity-0', 'pointer-events-none');
        }
        if(this.hasActionsWrapperTarget){
          this.actionsWrapperTarget.classList.add('opacity-0', 'pointer-events-none');
        }
      } else {
        wrapper.classList.remove('grid-template-rows-[0fr]');
        wrapper.classList.add('grid-template-rows-[1fr]');
        if(this.hasHideIconTarget && this.hasShowIconTarget){
          this.hideIconTarget.classList.remove('hidden');
          this.showIconTarget.classList.add('hidden', 'pointer-events-none');
        }
        if(this.hasSettingsModalTarget){
          this.settingsModalTarget.classList.remove('opacity-0', 'pointer-events-none');
        }
        if(this.hasSelectionWrapperTarget){
          this.selectionWrapperTarget.classList.remove('opacity-0', 'pointer-events-none');
        } 
        if(this.hasActionsWrapperTarget){
          this.actionsWrapperTarget.classList.remove('opacity-0', 'pointer-events-none');
        }
      }
    }
  }

  updateColumnVisibility() {
    if (!this.hasColumnVisibilityFormTarget) {
      return;
    }

    const form = this.columnVisibilityFormTarget;
    const columnVisibility = {};

    const checkboxes = form.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      const fieldName = checkbox.name;
      const isVisible = checkbox.checked;
      
      // Extract field name from the form name (remove form prefix if any)
      const columnField = fieldName.replace(/.*\[([^\]]+)\]/, '$1');

      columnVisibility[columnField] = isVisible;
    });

    if (this.grid) {
      Object.entries(columnVisibility).forEach(([field, isVisible]) => {
        this.grid.setColumnsVisible([field], isVisible);
      });
      this.updateColumnState();
    }
  }

  initializeTheme() {
    const existingMode = document.body.getAttribute('data-ag-theme-mode');
    
    if (!existingMode) {
      const themeValue = document.body.getAttribute('data-themes-theme-value');

      if (themeValue) {
        document.body.setAttribute('data-ag-theme-mode', themeValue);
      } else {
        const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        const systemTheme = isDarkMode ? 'dark' : 'light';
        document.body.setAttribute('data-ag-theme-mode', systemTheme);
      }
    }
  }

  async getLocaleText() {
    const currentLang = document.documentElement.getAttribute('lang') || 'en';
    
    const localeMap = {
      'ar': 'AG_GRID_LOCALE_EG',
      'bg': 'AG_GRID_LOCALE_BG',
      'zh_hk': 'AG_GRID_LOCALE_HK',
      'zh_cn': 'AG_GRID_LOCALE_CN',
      'zh_tw': 'AG_GRID_LOCALE_TW',
      'hr': 'AG_GRID_LOCALE_HR',
      'cs': 'AG_GRID_LOCALE_CZ',
      'da': 'AG_GRID_LOCALE_DK',
      'nl': 'AG_GRID_LOCALE_NL',
      'fi': 'AG_GRID_LOCALE_FI',
      'fr': 'AG_GRID_LOCALE_FR',
      'de': 'AG_GRID_LOCALE_DE',
      'el': 'AG_GRID_LOCALE_GR',
      'he': 'AG_GRID_LOCALE_IL',
      'hu': 'AG_GRID_LOCALE_HU',
      'it': 'AG_GRID_LOCALE_IT',
      'ja': 'AG_GRID_LOCALE_JP',
      'ko': 'AG_GRID_LOCALE_KR',
      'nb': 'AG_GRID_LOCALE_NO',
      'fa': 'AG_GRID_LOCALE_IR',
      'pl': 'AG_GRID_LOCALE_PL',
      'pt': 'AG_GRID_LOCALE_PT',
      'pt_br': 'AG_GRID_LOCALE_BR',
      'ro': 'AG_GRID_LOCALE_RO',
      'sk': 'AG_GRID_LOCALE_SK',
      'es': 'AG_GRID_LOCALE_ES',
      'sv': 'AG_GRID_LOCALE_SE',
      'tr': 'AG_GRID_LOCALE_TR',
      'uk': 'AG_GRID_LOCALE_UA',
      'ur': 'AG_GRID_LOCALE_PK',
      'vi': 'AG_GRID_LOCALE_VN'
    };

    const normalizedLang = currentLang.toLowerCase();
    const localeModule = localeMap[normalizedLang];

    if (localeModule) {
      try {
        const localeImport = await import('@ag-grid-community/locale');
        return localeImport[localeModule];
      } catch (error) {
        console.warn(`Failed to load locale for language '${currentLang}':`, error);
        return null; 
      }
    }

    // Return null for English or unsupported languages
    return null;
  }

  parseFilters(filters) {
    const parsed = {};

    for (const key in filters) {
      const filterObj = filters[key];

      if (filterObj.filterType === 'text' && 'filter' in filterObj) {
        parsed[key] = filterObj.filter;
      } else if (filterObj.filterType === 'set' && 'values' in filterObj) {
        parsed[key] = filterObj.values;
      } else if (filterObj.filterType === 'date' && 'dateFrom' in filterObj) {
        parsed[key] = {}
        parsed[key]['from'] = filterObj.dateFrom;
        parsed[key]['to'] = filterObj.dateTo;
        parsed[key]['type'] = filterObj.type;
      } else if (filterObj.filterType === 'number') {
        parsed[key] = {
          type: filterObj.type,
          value: filterObj.filter
        };
      } else if (filterObj.filterType === 'multi' && 'filterModels' in filterObj) {
        filterObj.filterModels.forEach((childFilter, index) => {
          if (childFilter !== null) {
            const fieldName = this.getChildFilterFieldName(key, index) || key;

            if (childFilter.filterType === 'text' && 'filter' in childFilter) {
              parsed[fieldName] = childFilter.filter;
            } else if (childFilter.filterType === 'set' && 'values' in childFilter) {
              parsed[fieldName] = childFilter.values;
            } else if (childFilter.filterType === 'date' && 'dateFrom' in childFilter) {
              parsed[fieldName] = {
                from: childFilter.dateFrom,
                to: childFilter.dateTo,
                type: childFilter.type
              };
            } else if (childFilter.filterType === 'number') {
              parsed[fieldName] = {
                type: childFilter.type,
                value: childFilter.filter
              };
            }
          }
        });
      }
    }

    return parsed;
  }

  getChildFilterFieldName(parentFieldName, childIndex) {
    if (this.multifilterFiltersMappingValue && this.multifilterFiltersMappingValue[parentFieldName]) {
      const mapping = this.multifilterFiltersMappingValue[parentFieldName];
      return mapping[childIndex];
    }
    
    return undefined;
  }

  isNotEmpty(value) {
    if (value === null || value === undefined) {
      return false;
    }
  
    if (typeof value.length === 'number') {
      return value.length > 0;
    }
    
    if (typeof value === 'number') {
      return !isNaN(value); // Returns false only for NaN
    }
  
    if (typeof value === 'object') {
      return Object.keys(value).length > 0;
    }
    
    return true;
  }

  autosizeOrderColumn() {
    if (this.autosizeOrderTimeout) {
      clearTimeout(this.autosizeOrderTimeout);
    }
    
    // Debounce the autosize to avoid performance issues with frequent data loads
    this.autosizeOrderTimeout = setTimeout(() => {
      if (this.grid) {
        const orderColumn = this.grid.getColumn('order');
        if (orderColumn) {
          this.grid.autoSizeColumns(['order'], { skipHeader: true });
        }
      }
    }, 50);
  }

  handleRowDragEnd(event) {
    const movingNodes = this.getDraggedRows(event);
    const overNode = event.overNode;
    
    if (!movingNodes.length || !overNode) return;
    
    // Don't move if dropped on one of the selected rows
    if (movingNodes.some(node => node === overNode)) return;
    
    const movingIds = movingNodes.map(node => node.data.id);
    this.updateMultipleRowOrder(movingIds, overNode.data.id, event.vDirection);
  }

  getDraggedRows(event) {
    const movingNode = event.node;
    const selectedNodes = this.grid.getSelectedNodes();
    
    // If the dragged row is selected and there are other selected rows, drag all selected
    if (selectedNodes.includes(movingNode) && selectedNodes.length > 1) {
      return selectedNodes;
    }
    
    return [movingNode];
  }

  addRowDragHighlight(rowNode) {
    if (!rowNode || !this.grid) return;
    
    let rowElement = rowNode.rowElement;

    if (!rowElement) {
      const rowId = String(rowNode.id);
      rowElement = this.tableTarget.querySelector(`[row-id="${rowId}"]`) || 
                   this.tableTarget.querySelector(`.ag-row[row-id="${rowId}"]`);
    }
    
    if (rowElement) {
      rowElement.classList.add('drag-hover-row', 'border', 'border-blue-400');
    }
  }

  removeRowDragHighlight(rowNode) {
    if (!rowNode || !this.grid) return;
    
    let rowElement = rowNode.rowElement;

    if (!rowElement) {
      const rowId = String(rowNode.id);
      rowElement = this.tableTarget.querySelector(`[row-id="${rowId}"]`) || 
                   this.tableTarget.querySelector(`.ag-row[row-id="${rowId}"]`);
    }
    
    if (rowElement) {
      rowElement.classList.remove('drag-hover-row', 'border', 'border-blue-400');
    }
  }

  async updateMultipleRowOrder(movingIds, targetId, direction) {
    if (!this.datasourceUrlValue || !movingIds.length) return;
    
    try {
      const urlParts = this.datasourceUrlValue.split('/');
      const profileId = urlParts[urlParts.indexOf('profiles') + 1];
      const loopId = new URLSearchParams(this.datasourceUrlValue.split('?')[1] || '').get('loop_id');
      
      if (!profileId || !loopId) {
        console.error('Could not extract profile_id or loop_id from datasource URL');
        return;
      }
      
      const targetNode = this.grid.getRowNode(String(targetId));
      if (!targetNode) {
        console.error('Target node not found');
        return;
      }
      
      const targetPosition = targetNode.data.order || 1;
      let newPosition = direction > 0 ? targetPosition + 1 : targetPosition;
      const formData = new FormData();
      
      movingIds.forEach((id, index) => {
        formData.append(`batch_loop_items_ids[${id}][position]`, newPosition + index);
      });
      
      // Add metadata for move event flash message
      formData.append('new_position', newPosition);
      formData.append('items_count', movingIds.length);
      
      const batchReorderUrl = `/profiles/${profileId}/loops/${loopId}/loop_items_batch_reorder`;
      
      const response = await fetch(batchReorderUrl, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html, application/json'
        },
        body: formData
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('turbo-stream')) {
          // Process the turbo-stream response to show the flash message
          const turboStreamHtml = await response.text();
          if (window.Turbo) {
            window.Turbo.renderStreamMessage(turboStreamHtml);
          }
        } 
        
        // Refresh the grid data to reflect the new order
        this.grid.refreshServerSide({ route: [], purge: true });
      } else {
        const errorText = await response.text();
        console.error('Failed to update row order:', errorText);
      }
    } catch (error) {
      console.error('Error updating row order:', error);
    }
  }
} 